import { useCallback, useEffect, useRef, useState } from 'react'

// ── Helpers ────────────────────────────────────────────────────

/**
 * Joue un oscillateur avec glide de fréquence et enveloppe gain.
 * @param {AudioContext} ctx
 * @param {OscillatorType} type
 * @param {number} t           temps de départ (ctx.currentTime)
 * @param {number} freqStart
 * @param {number} freqEnd
 * @param {number} duration    secondes
 * @param {number} gain        gain max (≤ 0.4 recommandé)
 * @param {number} attackTime  durée de l'attaque du gain
 */
function playOsc(ctx, type, t, freqStart, freqEnd, duration, gain = 0.3, attackTime = 0.01) {
  const osc      = ctx.createOscillator()
  const gainNode = ctx.createGain()

  osc.type = type
  osc.frequency.setValueAtTime(freqStart, t)
  if (freqEnd !== freqStart) {
    osc.frequency.exponentialRampToValueAtTime(freqEnd, t + duration)
  }

  gainNode.gain.setValueAtTime(0.001, t)
  gainNode.gain.linearRampToValueAtTime(gain, t + attackTime)
  gainNode.gain.exponentialRampToValueAtTime(0.001, t + duration)

  osc.connect(gainNode)
  gainNode.connect(ctx.destination)
  osc.start(t)
  osc.stop(t + duration + 0.05)
}

/**
 * Joue un bruit blanc avec decay exponentiel.
 * @param {AudioContext} ctx
 * @param {number} t       temps de départ
 * @param {number} duration
 * @param {number} gain
 */
function playNoise(ctx, t, duration, gain = 0.3) {
  const bufferSize = ctx.sampleRate
  const buffer     = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
  const data       = buffer.getChannelData(0)
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1

  const src      = ctx.createBufferSource()
  src.buffer     = buffer
  const gainNode = ctx.createGain()
  gainNode.gain.setValueAtTime(gain, t)
  gainNode.gain.exponentialRampToValueAtTime(0.001, t + duration)

  src.connect(gainNode)
  gainNode.connect(ctx.destination)
  src.start(t)
  src.stop(t + duration + 0.05)
}

// ── Sons "utilisation" ─────────────────────────────────────────

// 🍌 Boing descendant
function sound_use_banana(ctx) {
  const t = ctx.currentTime
  playOsc(ctx, 'sine', t, 400, 150, 0.5, 0.35, 0.01)
}

// 🐚 Tir rapide + ping
function sound_use_shell(ctx) {
  const t = ctx.currentTime
  playOsc(ctx, 'triangle', t, 300, 600, 0.15, 0.25, 0.005)
  playOsc(ctx, 'sine', t + 0.12, 800, 800, 0.3, 0.3, 0.005)
}

// 💣 Lancement lourd
function sound_use_bomb(ctx) {
  const t = ctx.currentTime
  playOsc(ctx, 'sine', t, 120, 60, 0.4, 0.4, 0.005)
  playNoise(ctx, t, 0.25, 0.2)
}

// 🪝 Zip rapide
function sound_use_hook(ctx) {
  const t = ctx.currentTime
  playOsc(ctx, 'sawtooth', t, 300, 900, 0.3, 0.2, 0.005)
}

// 🚀 Glissando montant
function sound_use_turbo(ctx) {
  const t = ctx.currentTime
  playOsc(ctx, 'sawtooth', t, 200, 800, 0.5, 0.25, 0.01)
}

// 🛡 Accord protecteur (ding doux)
function sound_use_shield(ctx) {
  const t = ctx.currentTime
  playOsc(ctx, 'sine', t, 660, 660, 0.6, 0.3, 0.06)
  playOsc(ctx, 'sine', t, 880, 880, 0.6, 0.2, 0.06)
}

// ⭐ Arpège scintillant (C5 E5 G5 C6)
function sound_use_star(ctx) {
  const t     = ctx.currentTime
  const notes = [523, 659, 784, 1047]
  notes.forEach((freq, i) => {
    playOsc(ctx, 'sine', t + i * 0.13, freq, freq, 0.18, 0.3, 0.005)
  })
}

// ❄️ Accord froid métallique descendant
function sound_use_freeze(ctx) {
  const t = ctx.currentTime
  playOsc(ctx, 'square', t, 400, 200, 0.5, 0.15, 0.01)
  playOsc(ctx, 'square', t, 800, 400, 0.5, 0.08, 0.01)
}

// ── Sons "réception" ───────────────────────────────────────────

// 🍌 Glissade comique
function sound_hit_banana(ctx) {
  const t = ctx.currentTime
  playOsc(ctx, 'sine', t, 600, 200, 0.4, 0.35, 0.005)
}

// 🐚 Impact + thud
function sound_hit_shell(ctx) {
  const t = ctx.currentTime
  playNoise(ctx, t, 0.2, 0.3)
  playOsc(ctx, 'sine', t, 200, 80, 0.3, 0.35, 0.005)
}

// 💣 Explosion
function sound_hit_bomb(ctx) {
  const t = ctx.currentTime
  playNoise(ctx, t, 0.7, 0.4)
  playOsc(ctx, 'sine', t, 80, 40, 0.7, 0.4, 0.005)
}

// 🪝 Arrachement
function sound_hit_hook(ctx) {
  const t = ctx.currentTime
  playOsc(ctx, 'sawtooth', t, 300, 100, 0.35, 0.3, 0.005)
  playNoise(ctx, t, 0.1, 0.2)
}

// ❄️ Cristallisation
function sound_hit_freeze(ctx) {
  const t = ctx.currentTime
  playOsc(ctx, 'square', t, 600, 200, 0.5, 0.2, 0.01)
  playOsc(ctx, 'square', t, 900, 300, 0.5, 0.1, 0.01)
  playNoise(ctx, t, 0.15, 0.15)
}

// ── Registre ───────────────────────────────────────────────────

const SOUNDS = {
  use_banana: sound_use_banana,
  use_shell:  sound_use_shell,
  use_bomb:   sound_use_bomb,
  use_hook:   sound_use_hook,
  use_turbo:  sound_use_turbo,
  use_shield: sound_use_shield,
  use_star:   sound_use_star,
  use_freeze: sound_use_freeze,
  hit_banana: sound_hit_banana,
  hit_shell:  sound_hit_shell,
  hit_bomb:   sound_hit_bomb,
  hit_hook:   sound_hit_hook,
  hit_freeze: sound_hit_freeze,
}

const STORAGE_KEY = 'kq_muted'

// ── Hook ───────────────────────────────────────────────────────

export function useSoundPlayer() {
  const ctxRef = useRef(null)
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === 'true' } catch { return false }
  })

  // Création/reprise lazy de l'AudioContext (satisfait la contrainte geste iOS)
  function getCtx() {
    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)()
    }
    if (ctxRef.current.state === 'suspended') {
      ctxRef.current.resume()
    }
    return ctxRef.current
  }

  const playSound = useCallback((key) => {
    if (muted) return
    const fn = SOUNDS[key]
    if (!fn) return
    try {
      fn(getCtx())
    } catch (e) {
      // Les erreurs audio ne doivent jamais crasher l'interface de jeu
      if (import.meta.env.DEV) console.warn('[useSoundPlayer]', key, e)
    }
  }, [muted]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMute = useCallback(() => {
    setMuted(prev => {
      const next = !prev
      try { localStorage.setItem(STORAGE_KEY, String(next)) } catch {}
      return next
    })
  }, [])

  useEffect(() => {
    return () => {
      ctxRef.current?.close()
      ctxRef.current = null
    }
  }, [])

  return { playSound, muted, toggleMute }
}
